import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import Layout from '../../components/Layout';
import { privacy, PRIV_COLOR } from '../../lib/privacyApi';
import { ShieldCheck, Trash2, Send } from 'lucide-react';

const ic = 'w-full px-3 py-2 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-500';
const Badge = ({ s }) => <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${PRIV_COLOR[s] || 'bg-slate-100 text-slate-700'}`}>{String(s || '').replace(/_/g, ' ')}</span>;

export default function PrivacyMyData() {
  const [mine, setMine] = useState([]);
  const [type, setType] = useState('ACCESS');
  const [desc, setDesc] = useState('');
  const [preview, setPreview] = useState(null);
  const [confirming, setConfirming] = useState(false);

  const load = useCallback(() => privacy.myRequests().then(setMine).catch(() => {}), []);
  useEffect(() => { load(); privacy.deletionPreview().then(setPreview).catch(() => {}); }, [load]);

  const submit = async () => {
    try { await privacy.createRequest({ requestType: type, description: desc }); toast.success('Request submitted'); setDesc(''); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Submit failed'); }
  };
  const requestDeletion = async () => {
    try { await privacy.requestDeletion({ reason: 'User-initiated from Privacy & My Data' }); toast.success('Account deletion request submitted'); setConfirming(false); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  return (
    <Layout>
      <div className="space-y-6 max-w-3xl" data-testid="privacy-my-data">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2"><ShieldCheck className="w-7 h-7 text-indigo-600" /> Privacy &amp; My Data</h1>
          <p className="text-slate-600 mt-1 text-sm">Submit a privacy request or ask to delete your account. Requests are handled through a governed workflow.</p>
        </div>

        {/* Submit request */}
        <div className="bg-white rounded-xl border border-slate-100 p-5 space-y-3" data-testid="privacy-request-form">
          <h2 className="text-base font-semibold text-slate-800">Submit a privacy request</h2>
          <select className={ic} value={type} onChange={(e) => setType(e.target.value)} data-testid="myreq-type">
            {['ACCESS', 'CORRECTION', 'UPDATE', 'ERASURE', 'CONSENT_WITHDRAWAL', 'GRIEVANCE', 'OTHER'].map((t) => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
          </select>
          <textarea className={ic} rows={3} placeholder="Describe your request (optional for most types)" value={desc} onChange={(e) => setDesc(e.target.value)} data-testid="myreq-desc" />
          <button onClick={submit} className="inline-flex items-center gap-1.5 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700" data-testid="myreq-submit"><Send className="w-4 h-4" /> Submit request</button>
        </div>

        {/* Account deletion */}
        <div className="bg-white rounded-xl border border-rose-100 p-5 space-y-3" data-testid="account-deletion-section">
          <h2 className="text-base font-semibold text-rose-700 flex items-center gap-2"><Trash2 className="w-4 h-4" /> Delete my account</h2>
          {preview && (
            <div className="text-xs bg-amber-50 border border-amber-100 rounded-lg p-3 text-amber-800" data-testid="deletion-preview">
              <strong>What happens:</strong> {preview.explanation}
              <div className="mt-1">Governed outcome: <strong>{String(preview.recommendedOutcome).replace(/_/g, ' ')}</strong></div>
            </div>
          )}
          <p className="text-xs text-slate-500">Account deactivation alone is not deletion. Some records may be retained or de-identified to meet contractual/legal obligations. [LEGAL REVIEW REQUIRED]</p>
          {!confirming ? (
            <button onClick={() => setConfirming(true)} className="inline-flex items-center gap-1.5 px-4 py-2 bg-rose-600 text-white rounded-lg text-sm hover:bg-rose-700" data-testid="deletion-initiate-btn"><Trash2 className="w-4 h-4" /> Request account deletion</button>
          ) : (
            <div className="flex items-center gap-2" data-testid="deletion-confirm-row">
              <button onClick={requestDeletion} className="px-4 py-2 bg-rose-600 text-white rounded-lg text-sm hover:bg-rose-700" data-testid="deletion-confirm-btn">Confirm deletion request</button>
              <button onClick={() => setConfirming(false)} className="px-4 py-2 bg-slate-100 text-slate-600 rounded-lg text-sm hover:bg-slate-200" data-testid="deletion-cancel-btn">Cancel</button>
            </div>
          )}
        </div>

        {/* My requests */}
        <div className="bg-white rounded-xl border border-slate-100 overflow-x-auto" data-testid="my-requests-list">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr><th className="text-left px-4 py-2.5">Number</th><th className="text-left px-4 py-2.5">Type</th><th className="text-left px-4 py-2.5">Status</th></tr></thead>
            <tbody>
              {mine.map((r) => (
                <tr key={r.id} className="border-t border-slate-50" data-testid={`myreq-row-${r.id}`}>
                  <td className="px-4 py-2.5 font-mono text-xs text-indigo-800">{r.number}</td>
                  <td className="px-4 py-2.5 text-slate-700">{r.requestType.replace(/_/g, ' ')}</td>
                  <td className="px-4 py-2.5"><Badge s={r.status} /></td>
                </tr>
              ))}
              {mine.length === 0 && <tr><td colSpan={3} className="px-4 py-8 text-center text-slate-400" data-testid="myreq-empty">You have no privacy requests yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
